import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import MapContext from "../Map/MapContext";
import Draw, { createBox } from "ol/interaction/Draw";
import Select from "ol/interaction/Select";
import styled from "styled-components";
import get from "lodash/get";
import { injectIntl } from "react-intl";

const Abs = styled.div`
  position: absolute;
  bottom: ${(p) => (p.addressSearchEnabled ? 0 : null)};
`;

const ToolbarWrapper = styled.div`
  position: relative;
  z-index: 1;
  margin: 10px;
  background-color: rgb(255, 255, 255);
  padding: 5px 6px;
  border-radius: 5px;
  min-height: 35px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 992px) {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    gap: 3px;
    margin: 10px 3px;
    padding: 3px 3px;
  }
`;

const StyledBtn = styled.div`
  font-size: 24px;
  padding: 0 3px;

  @media (max-width: 992px) {
    display: flex;
    padding: 3px 0px;
    max-width: 24px;
    max-height: 24px;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
  }

  &:hover {
    color: lightblue;
  }
`;

const TooltipBelow = styled.div`
  position: absolute;
  background-color: white;
  top: 40px;
  left: 0;
  font-size: 14px;
  width: max-content;
  padding: 5px;
  border: none;

  &:before {
    content: "";
    display: block;
    position: absolute;
    width: 0;
    height: 0;
    top: -4px;
    left: ${(p) => `${p.parentOffsetLeftValue + 7}px`};
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid white;
  }
`;

const TooltipAbove = styled(TooltipBelow)`
  top: -32px;

  &:before {
    top: 26px;
    border-top: 5px solid white;
    border-bottom: none;
  }
`;

const TooltipBeside = styled(TooltipBelow)`
  @media (max-width: 992px) {
    background-color: inherit;
    position: initial;
    font-size: 12px;
    width: max-content;
    padding: 5px;
    border: none;
    top: 0;
    margin-left: 10px;
  }

  &:before {
    position: relative;
    content: "";
    display: block;
  }
`;

const CloseDrawMode = styled.button`
  padding: 6px;
  background-color: steelblue;
  color: white;

  :disabled {
    opacity: 0.8;
  }
`;

const ButtonWrapper = styled.div`
  &[data-id="showExplanation"] {
    display: none;
  }
  @media (max-width: 992px) {
    display: flex;
    overflow: hidden;
    align-items: center;

    &[data-id="showExplanation"] {
      display: flex;

      div:first-child {
        font-size: 18px;
      }
    }

    /*  The drawing tool doesn't work especially well on phones and tablet, so until we develop
    better functionality, I say we just hide it. */
    &[data-id="drawLine"] {
      display: none;
    }
  }
`;

const ToolbarBtn = (props) => {
  const {
    icon,
    onClick,
    type,
    freehand,
    tooltip,
    addressSearchEnabled,
    showToolHelp,
    id,
  } = props;
  const [isMouseOver, setIsMouseOver] = useState(false);
  const ref = useRef();
  const isSmallScreen = window.matchMedia("(max-width: 992px)")?.matches;
  const Tooltip =
    showToolHelp && isSmallScreen
      ? TooltipBeside
      : addressSearchEnabled
      ? TooltipAbove
      : TooltipBelow;

  return (
    <ButtonWrapper ref={ref} data-id={id} showToolHelp={showToolHelp}>
      <StyledBtn
        data-freehand={freehand}
        data-type={type}
        className={icon}
        onClick={onClick}
        title={tooltip}
        onMouseEnter={() => setIsMouseOver(true)}
        onMouseLeave={() => setIsMouseOver(false)}
      />
      {(isMouseOver || (showToolHelp && isSmallScreen)) && (
        <Tooltip parentOffsetLeftValue={ref.current?.offsetLeft}>
          {tooltip}
        </Tooltip>
      )}
    </ButtonWrapper>
  );
};

const DrawModeLabel = injectIntl((props) => {
  const { intl, onClick } = props;
  return (
    <CloseDrawMode onClick={onClick}>
      {intl.formatMessage({ id: "OLMap.InDrawMode" })}
    </CloseDrawMode>
  );
});

const Interaction = injectIntl(
  ({
    source,
    parentPosition,
    addressSearchEnabled,
    intl,
    disablePoint,
    disableBox,
    disableLine,
    disablePen,
    disableEraser,
    setStop,
    drawMode,
    setDrawMode,
    setLocationFeatures = () => {},
  }) => {
    const { map } = useContext(MapContext);
    const [draw, setDraw] = useState(false);
    const debounceRef = useRef();
    const [showToolHelp, setShowToolHelp] = useState(false);

    useEffect(() => {
      if (!map) return;

      const view = map.getView();

      const onChange = () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
      };

      view.on("change", onChange);

      return () => {
        try {
          if (debounceRef.current) {
            clearTimeout(debounceRef.current);
          }

          view.off("change", onChange);
        } catch (err) {
          console.error(err);
        }
      };
    }, [map, drawMode]);

    /**
     * called when we want to draw on the map (interaction is the openlayer word for drawing)
     *
     * Note: that we save the draw in state, as we need this to remove the interaction (see disableInteraction).
     * Note: Special case for remove, we create a select interaction and remove the feature using the 'add'-event.
     */
    const enableInteraction = useCallback(
      (e) => {
        const type = get(e, "currentTarget.dataset.type");
        const freehand = get(e, "currentTarget.dataset.freehand");

        if (!map) return;
        if (type) {
          setShowToolHelp(false);
        }

        let geometryFunction;
        let _type;

        switch (type) {
          case "Point": {
            _type = type;
            break;
          }
          case "Box": {
            geometryFunction = createBox();
            _type = "Circle";
            break;
          }
          case "LineString": {
            _type = type;
            break;
          }
          case "Remove": {
            const selectSingleClick = new Select();
            selectSingleClick.getFeatures().on("add", function (feature) {
              if (feature?.element.values_?.category === "Adress") {
                setLocationFeatures([]);
              }
              source.removeFeature(feature.element);
              feature.target.remove(feature.element);
            });
            map.addInteraction(selectSingleClick);
            setDrawMode(type);
            setDraw(selectSingleClick);
            return;
          }
        }

        const draw = new Draw({
          source,
          type: _type,
          freehand,
          geometryFunction,
        });

        map.addInteraction(draw);

        setDraw(draw);
        setDrawMode(type);
        setStop({ next: true });
      },
      [map, source]
    );

    /**
     * Called once we stop drawing
     */
    const disableInteraction = useCallback(() => {
      map.removeInteraction(draw);
      setDrawMode(null);
      setDraw(null);
    }, [draw]);

    return (
      <Abs
        parentPosition={parentPosition}
        addressSearchEnabled={addressSearchEnabled}
      >
        {}
        <ToolbarWrapper>
          {drawMode && <DrawModeLabel onClick={disableInteraction} />}
          {!drawMode && (
            <>
              <ToolbarBtn
                id="showExplanation"
                icon="fal fa-question-circle"
                onClick={() => setShowToolHelp(!showToolHelp)}
                addressSearchEnabled={addressSearchEnabled}
                tooltip={intl.formatMessage({
                  id: "OLMap.Interaction.showToolHelp",
                })}
                showToolHelp={showToolHelp}
              />
              {!disablePoint && (
                <ToolbarBtn
                  type="Point"
                  id="addCircle"
                  icon="fal fa-dot-circle"
                  onClick={enableInteraction}
                  tooltip={intl.formatMessage({
                    id: "OLMap.Interaction.markPoint",
                  })}
                  addressSearchEnabled={addressSearchEnabled}
                  showToolHelp={showToolHelp}
                />
              )}
              {!disableBox && (
                <ToolbarBtn
                  type="Box"
                  id="addBox"
                  icon="fal fa-square"
                  onClick={enableInteraction}
                  tooltip={intl.formatMessage({
                    id: "OLMap.Interaction.markRectangle",
                  })}
                  addressSearchEnabled={addressSearchEnabled}
                  showToolHelp={showToolHelp}
                />
              )}
              {!disableLine && (
                <ToolbarBtn
                  type="LineString"
                  id="addLine"
                  icon="fal fa-horizontal-rule"
                  onClick={enableInteraction}
                  tooltip={intl.formatMessage({
                    id: "OLMap.Interaction.markLine",
                  })}
                  addressSearchEnabled={addressSearchEnabled}
                  showToolHelp={showToolHelp}
                />
              )}
              {!disablePen && (
                <ToolbarBtn
                  type="LineString"
                  id="drawLine"
                  freehand={true}
                  icon="fal fa-pen"
                  onClick={enableInteraction}
                  tooltip={intl.formatMessage({
                    id: "OLMap.Interaction.drawLine",
                  })}
                  addressSearchEnabled={addressSearchEnabled}
                  showToolHelp={showToolHelp}
                />
              )}
              {!disableEraser && (
                <ToolbarBtn
                  type="Remove"
                  id="remove"
                  freehand={true}
                  icon="fal fa-eraser"
                  onClick={enableInteraction}
                  tooltip={intl.formatMessage({
                    id: "OLMap.Interaction.removeMark",
                  })}
                  addressSearchEnabled={addressSearchEnabled}
                  showToolHelp={showToolHelp}
                />
              )}
            </>
          )}
        </ToolbarWrapper>
      </Abs>
    );
  }
);

export default injectIntl(Interaction);
